import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { RideSoftValidationErrorList } from "@ryde/controls";
import { useDragRidesPassengers, useModalConfirm } from "@ryde/redux";
import { ConfirmResult, movePassengersErrorAtom } from "@ryde/shared";

import { ErrorsBlockStyled } from "./rides-modals.styled";

export const useRideSoftValidationModal = (): null => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const [movePassengersError, setMovePassengersError] = useRecoilState(
    movePassengersErrorAtom,
  );
  const { movePassengersFromRideToRide } = useDragRidesPassengers();

  const showModalIfSoftValidationFailed = useCallback(async () => {
    if (!movePassengersError) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t(`general.VERIFICATION_REQUIRED`),
      customContent: (
        <ErrorsBlockStyled>
          <RideSoftValidationErrorList
            validationErrors={
              movePassengersError.response.validationErrors ?? []
            }
          />
        </ErrorsBlockStyled>
      ),
      okButtonTitle: t(`general.OK`),
      cancelButtonTitle: t(`general.CANCEL`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const { fromRideId, toRideId, passengers } = movePassengersError.request;

      await movePassengersFromRideToRide({
        fromRideId,
        toRideId,
        passengers,
        token: movePassengersError.response.token,
      });
    } else {
      setMovePassengersError(null);
    }
  }, [
    confirmDialogResult,
    movePassengersError,
    movePassengersFromRideToRide,
    setMovePassengersError,
    t,
  ]);

  useEffect(() => {
    showModalIfSoftValidationFailed();
  }, [showModalIfSoftValidationFailed]);

  return null;
};
